import { Autocomplete, Box, Button, CircularProgress, Divider, Grid, IconButton, Stack, TextField, Typography } from "@mui/material";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import CheckoutForm from "./CheckoutForm";
import ApplePayIcon from "../../assets/ApplePayIcon.svg";
import GooglePayIcon from "../../assets/GooglePayIcon.svg";
import StripeCards from "./StripeCards";
import { useSafeAccessAuth0Token } from "src/hooks/useSafeAccessAuth0Token";
import { verifyApplePayment } from "src/api/verifyApplePayment";
import { verifyGooglePayment } from "src/api/verifyGooglePayment";

import { getProducts, isAndroidNative, isIosNative, purchaseProduct } from "src/services/iap";
import { useContext, useState } from "react";
import PaymentDialog from "./PaymentDialog";
import RedirectUsertoAppDialog from "./RedirectUsertoAppDialog";
import { WidgetContext } from "src/contexts/WidgetContext";
import { isMobileWebBrowser, mobile } from "src/utils/platformUtils";
import { createCheckoutSession } from "src/api/createCheckoutSession";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import { isPlatform } from "@ionic/react";
import { useAuth0 } from "@auth0/auth0-react";
import { getProductConfig } from "src/config/products.config";
import { planDurationFromProductId, trackBeginCheckout, trackPurchase, trackSubscriptionFailed } from "src/utils/analyticsCapFirebase";

// Load Stripe with environment variable
// const stripePromise = loadStripe(process.env.STRIPE_PUBLIC_KEY || "");

const AddPaymentMethod = ({ onClose, onBack, onChangeContent, setActiveComponent, onPaymentOpenChange, selectedProductId, promotionCodeId, promoPercentOff, promoAmountOff }) => {
  const { setCredits, credits, setFreeCredits } = useContext(WidgetContext);
  const { getSafeAccessAuth0Token } = useSafeAccessAuth0Token();
  const { user } = useAuth0();
  const analyticsAuthId = user?.sub || "unknown";
  const [showRedirectDialog, setShowRedirectDialog] = useState(false);
  const [showStripeForm, setShowStripeForm] = useState(false);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentDialogSuccess, setPaymentDialogSuccess] = useState(false);
  const [paymentDialogMessage, setPaymentDialogMessage] = useState<string | undefined>(undefined);

  const isMobile = isMobileWebBrowser() || mobile;

  // Lazy load Stripe only when needed
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

  const handleApplePayClick = async () => {
    console.log("🍎 Apple Pay clicked");
    console.log("isIosNative():", isIosNative());
    console.log("🔍 selectedProductId received:", selectedProductId);

    try {
      if (isIosNative()) {
        console.log("📱 Running on iOS native");
        // Check if IAP plugin is available
        const isPluginAvailable = !!(window as any).Capacitor?.Plugins?.IAPPlugin;
        console.log("IAP plugin present?", isPluginAvailable);

        if (!isPluginAvailable) {
          // alert("Payment system not available. Please update the app.");
          console.error("Payment system not available");
          return;
        }

        // Use selectedProductId or fallback to default
        const productIdToPurchase = selectedProductId || "league_connect_standard_monthly";
        console.log("🎯 Purchasing product:", productIdToPurchase);
        console.log("🔍 selectedProductId was:", selectedProductId, "| Using:", productIdToPurchase);

        // Verify product is available before purchase
        try {
          const products = await getProducts([productIdToPurchase]);
          if (!products || products.length === 0) {
            // alert("Product not available at this time.");
            console.error("Product not available:", productIdToPurchase);
            return;
          }
          console.log("Product loaded:", products[0]);
        } catch (productError) {
          console.error("Failed to load product:", productError);
          // alert("Could not load product details. Please try again.");
          return;
        }

        // Initiate purchase with selected product
        console.log("Starting purchase flow...");
        void trackBeginCheckout(analyticsAuthId, productIdToPurchase, planDurationFromProductId(productIdToPurchase), getProductConfig(productIdToPurchase)?.price ?? 0);
        const purchaseResult = await purchaseProduct(productIdToPurchase);

        if (purchaseResult?.status === "success") {
          console.log("Purchase successful:", purchaseResult);

          // Get authentication token
          const accessAuth0Token = await getSafeAccessAuth0Token();
          if (!accessAuth0Token) {
            console.error("No auth token available");
            // alert("Authentication error. Please log in again.");
            return;
          }

          // Prefer JWS when available, otherwise send transactionId (avoid falsy 0 issue)
          const jws = (purchaseResult as any).jws as string | undefined;
          const txId = (purchaseResult as any).transactionId as string | number | undefined;
          const receiptPayload = jws && jws.length > 0 ? jws : String(txId ?? "");

          console.log("Verifying purchase with backend...");
          console.log("Using JWS:", jws && jws.length > 0 ? "Yes" : "No (using transaction ID)");
          console.log("Product ID:", productIdToPurchase);

          // Verify with backend - always send productId so backend knows which product was purchased
          const verifyResult = await verifyApplePayment(accessAuth0Token, receiptPayload, productIdToPurchase);

          if (verifyResult?.success) {
            console.log("✅ Payment verified successfully:", verifyResult);
            void trackPurchase(analyticsAuthId, "apple_iap", productIdToPurchase, getProductConfig(productIdToPurchase)?.price ?? 0);

            // Update credits in UI if provided by backend
            if (verifyResult.credits !== undefined) {
              setCredits(verifyResult.credits);
              console.log("✅ Credits updated in UI:", verifyResult.credits);
            }

            // alert(`Subscription activated! Expires: ${new Date(verifyResult.expirationDate || "").toLocaleDateString()}`);
            // Close the payment drawer after success
            // StoreKit already showed success UI, just close drawer
            onClose();
          } else {
            console.error("❌ Server verification failed:", verifyResult?.message);
            // alert(`Verification failed: ${verifyResult?.message || "Unknown error"}`);
          }
        } else if (purchaseResult?.status === "pending") {
          console.log("Purchase pending confirmation");
          // alert("Purchase is pending. You'll be notified when it's confirmed.");
          // StoreKit will handle pending state UI
        }
        return;
      }

      // User is on web - show redirect dialog to download the app
      console.log("🌐 Running on web, showing redirect dialog");
      setShowRedirectDialog(true);
      console.log("showRedirectDialog set to:", true);
    } catch (error) {
      console.error("handleApplePayClick error:", error);

      // Handle different error types
      const errorMessage = error?.message || String(error);

      if (errorMessage.includes("cancelled") || errorMessage.includes("User cancelled")) {
        console.log("User cancelled the purchase");
        // Don't show error for user cancellation - StoreKit handles this
        return;
      }

      // Log errors but let StoreKit handle the UI
      if (errorMessage.includes("Authentication")) {
        // alert("Authentication error. Please log in again.");
        console.error("Authentication error");
      } else if (errorMessage.includes("Network") || errorMessage.includes("network")) {
        // alert("Network error. Please check your connection and try again.");
        console.error("Network error");
      } else if (errorMessage.includes("not available")) {
        // alert("In-app purchases are not available. Please try again later.");
        console.error("In-app purchases not available");
        // If product not available, show redirect dialog as fallback
        setShowRedirectDialog(true);
      } else {
        // alert(`Payment error: ${errorMessage}`);
        console.error("Payment error:", errorMessage);
      }
    }
  };

  const handleGooglePayClick = () => {
    console.log("🔵 Google Pay clicked");

    // Google Play Billing only available on Android native
    const isAndroid = isAndroidNative();
    console.log("isAndroidNative():", isAndroid);
    console.log("🔍 selectedProductId received:", selectedProductId);

    if (!isAndroid) {
      // Web / iOS: show redirect dialog
      setShowRedirectDialog(true);
      console.log("showRedirectDialog set to:", true);
      return;
    }

    (async () => {
      try {
        // Check if IAP plugin is available
        const isPluginAvailable = !!(window as any).Capacitor?.Plugins?.IAPPlugin;
        console.log("IAP plugin present?", isPluginAvailable);

        if (!isPluginAvailable) {
          console.error("Payment system not available (IAPPlugin missing)");
          return;
        }

        const productIdToPurchase = selectedProductId || "league_connect_standard_monthly";
        console.log("🎯 Purchasing product:", productIdToPurchase);

        // Verify product is available before purchase
        try {
          const products = await getProducts([productIdToPurchase]);
          if (!products || products.length === 0) {
            console.error("Product not available:", productIdToPurchase);
            return;
          }
          console.log("Product loaded:", products[0]);
        } catch (productError) {
          console.error("Failed to load product:", productError);
          return;
        }

        console.log("Starting purchase flow...");
        void trackBeginCheckout(analyticsAuthId, productIdToPurchase, planDurationFromProductId(productIdToPurchase), getProductConfig(productIdToPurchase)?.price ?? 0);
        const purchaseResult = await purchaseProduct(productIdToPurchase);

        if (purchaseResult?.status === "success") {
          console.log("Purchase successful:", purchaseResult);

          const accessAuth0Token = await getSafeAccessAuth0Token();
          if (!accessAuth0Token) {
            console.error("No auth token available");
            return;
          }

          // Android: transactionId is the purchaseToken (see IAPPlugin.java)
          const purchaseToken = (purchaseResult as any).purchaseToken || (purchaseResult as any).transactionId || "";

          if (!purchaseToken) {
            console.error("Missing purchaseToken from purchase result");
            return;
          }

          console.log("Verifying purchase with backend...");
          console.log("Product ID:", productIdToPurchase);

          const verifyResult = await verifyGooglePayment(accessAuth0Token, String(purchaseToken), productIdToPurchase);

          if (verifyResult?.success) {
            console.log("✅ Payment verified successfully:", verifyResult);
            void trackPurchase(analyticsAuthId, "google_iap", productIdToPurchase, getProductConfig(productIdToPurchase)?.price ?? 0);

            if (verifyResult.credits !== undefined) {
              setCredits(verifyResult.credits);
              console.log("✅ Credits updated in UI:", verifyResult.credits);
            }

            onClose();
          } else {
            console.error("❌ Server verification failed:", verifyResult?.message);
            setPaymentDialogSuccess(false);
            setPaymentDialogMessage(verifyResult?.message || "Google Play verification failed.");
            setShowPaymentDialog(true);
          }
        } else if (purchaseResult?.status === "pending") {
          console.log("Purchase pending confirmation");
        }
      } catch (error) {
        console.error("handleGooglePayClick error:", error);
        const errorMessage = error?.message || String(error);
        if (errorMessage.includes("cancelled") || errorMessage.includes("User cancelled")) {
          console.log("User cancelled the purchase");
          return;
        }
      }
    })();
  };

  // Handle Credit Card Click - Redirect to Stripe Checkout
  const handleCreditCardClick = async () => {
    console.log("💳 Credit Card clicked");

    if (!selectedProductId) {
      console.error("No product selected");
      return;
    }

    // In iOS native, route the payment action to Apple purchase flow
    // instead of Stripe Checkout redirection.
    if (isIosNative()) {
      await handleApplePayClick();
      return;
    }

    // Set loading state
    setIsCreatingCheckout(true);

    try {
      // Get authentication token
      const accessAuth0Token = await getSafeAccessAuth0Token();
      if (!accessAuth0Token) {
        console.error("❌ No auth token");
        setIsCreatingCheckout(false);
        alert("Authentication error. Please log in again.");
        return;
      }

      // Get current URL for success/cancel redirects
      // Check if we're in Capacitor context (native app OR Capacitor Browser)
      const urlParams = new URLSearchParams(window.location.search);
      const fromAppUrl = urlParams.get("fromApp") === "true";
      const isNativePlatform = Capacitor.isNativePlatform();
      const isHybridPlatform = isPlatform("hybrid");

      // Check sessionStorage for fromApp (persists across navigation within Capacitor Browser)
      const fromAppSession = sessionStorage.getItem("fromApp") === "true";

      // If fromApp is in URL, save it to sessionStorage for future use
      if (fromAppUrl && !fromAppSession) {
        sessionStorage.setItem("fromApp", "true");
      }

      // fromApp is true if it's in URL OR in sessionStorage
      const fromApp = fromAppUrl || fromAppSession;

      // PRECISE detection for adding source=app:
      // - fromApp=true (in Capacitor Browser opened from native)
      // - isNativePlatform/isHybridPlatform (directly in native app)
      // DO NOT use hasCapacitor here - it can be true in web desktop too!
      const isCapacitorContext = fromApp || isNativePlatform || isHybridPlatform;

      console.log("🔍 AddPaymentMethod: Detection", { fromAppUrl, fromAppSession, fromApp, isNativePlatform, isHybridPlatform, isCapacitorContext });

      let baseUrl: string;
      if (isCapacitorContext) {
        // In Capacitor: use window.location.origin to stay on same server
        baseUrl = window.location.origin;
        console.log("📱 Capacitor context: Using /stripe-return for auto-close, baseUrl:", baseUrl);
      } else {
        // In web desktop: use current origin and redirect to /home
        baseUrl = window.location.origin;
        console.log("🌐 Web context: Using /home redirect, baseUrl:", baseUrl);
      }

      // Add source=app parameter if in Capacitor context to force redirect to native scheme
      const sourceParam = isCapacitorContext ? "&source=app" : "";

      const successUrl = isCapacitorContext
        ? `${baseUrl}/stripe-return?payment=success&productId=${selectedProductId}${sourceParam}`
        : `${baseUrl}/home?payment=success&productId=${selectedProductId}`;
      const cancelUrl = isCapacitorContext
        ? `${baseUrl}/stripe-return?payment=cancelled&productId=${selectedProductId || ""}${sourceParam}`
        : `${baseUrl}/home?payment=cancelled&productId=${selectedProductId || ""}`;

      console.log("🔄 Creating Checkout Session:", { successUrl, cancelUrl, isCapacitorContext });

      // Create Checkout Session
      void trackBeginCheckout(analyticsAuthId, selectedProductId, planDurationFromProductId(selectedProductId), getProductConfig(selectedProductId)?.price ?? 0);
      const result = await createCheckoutSession(accessAuth0Token, selectedProductId, successUrl, cancelUrl, promotionCodeId);

      if (result.success && result.url) {
        console.log("✅ Checkout Session created, redirecting to:", result.url);

        if (isCapacitorContext) {
          // In Capacitor context (native or browser): just redirect, Stripe will return to /stripe-return
          console.log("📱 Capacitor context: Redirecting to Stripe in same window");
          window.location.href = result.url;
        } else {
          console.log("🌐 Web platform detected, redirecting to Stripe Checkout");
          // On web, redirect normally (don't reset loading state as we're redirecting)
          window.location.href = result.url;
        }
      } else {
        console.error("❌ Failed to create Checkout Session:", result.message);
        void trackSubscriptionFailed(analyticsAuthId, selectedProductId || "unknown", "failure", "checkout_session_failed");
        setIsCreatingCheckout(false);
        setPaymentDialogSuccess(false);
        setPaymentDialogMessage(result.message || "Failed to create payment session. Please try again.");
        setShowPaymentDialog(true);
      }
    } catch (error) {
      console.error("Error creating Checkout Session:", error);
      setIsCreatingCheckout(false);
      setPaymentDialogSuccess(false);
      setPaymentDialogMessage("An error occurred. Please try again.");
      setShowPaymentDialog(true);
    }
  };

  // ============================================================
  // COMMENTED OUT: Old Credit Card Click handler (Stripe Elements)
  // Uncomment this and comment out the above to use Stripe Elements instead
  // ============================================================

  // const handleCreditCardClick = () => {
  //   console.log("💳 Credit Card clicked");

  //   if (!selectedProductId) {
  //     console.error("No product selected");
  //     return;
  //   }

  //   // Get Stripe public key from environment (must start with REACT_APP_)
  //   const stripePublicKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY;

  //   if (!stripePublicKey || stripePublicKey.trim() === "") {
  //     console.error("❌ REACT_APP_STRIPE_PUBLIC_KEY is not set in environment variables");
  //     alert("Stripe payment is not configured. Please contact support.");
  //     return;
  //   }

  //   // Lazy load Stripe only when user clicks Credit Card
  //   if (!stripePromise) {
  //     console.log("📦 Loading Stripe.js...");
  //     setStripePromise(loadStripe(stripePublicKey));
  //   }

  //   setShowStripeForm(true);
  // };

  // Handle Stripe Success
  const handleStripeSuccess = (updatedCredits: number) => {
    console.log("✅ Stripe payment successful, credits:", updatedCredits);
    setCredits(updatedCredits);
    // DON'T close the form immediately - let CheckoutForm handle the close after showing check icon
    // setShowStripeForm(false); // Removed - CheckoutForm will close itself via onDrawerClose
  };

  const goBackToManageMembership = () => {
    setActiveComponent("manageMembership");
    onPaymentOpenChange?.(true);
  };

  return (
    <>
      <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" p={1}>
        <IconButton aria-label="close" onClick={goBackToManageMembership}>
          <ArrowBackIcon sx={{ color: `${PureLightThemeCaaS.colors.alpha.black[100]}` }} />
        </IconButton>
        <Typography variant="h4" pl={1} color={`${PureLightThemeCaaS.colors.alpha.black[100]}`} textAlign={"center"}>
          Add a payment method
        </Typography>
        <Box display="flex" flexDirection="row" justifyContent="flex-end" alignItems="center">
          <IconButton aria-label="close" onClick={onClose}>
            <CloseIcon sx={{ color: `${PureLightThemeCaaS.colors.alpha.black[100]}` }} />
          </IconButton>
        </Box>
      </Box>

      {/* <StripeCards onClose={onClose} onBack={onBack} /> */}

      {/* <Box onClick={() => onChangeContent("stripeCards")} display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" p={1} sx={{ cursor: "pointer" }}>
        <Typography variant="h6" pl={1} color={`${PureLightThemeCaaS.colors.alpha.white[100]}`} textAlign={"center"}>
          Add a New Card
        </Typography>
        <Box display="flex" flexDirection="row" justifyContent="flex-end" alignItems="center">
          <IconButton aria-label="close">
            <AddIcon sx={{ color: `${PureLightThemeCaaS.colors.alpha.white[100]}` }} />
          </IconButton>
        </Box>
      </Box> */}

      {/* Divider for separation between the title and the cards */}
      <Divider
        sx={{
          margin: {
            xs: "auto",
            md: "0px",
          },
          width: "100%",
          bgcolor: "#000000",
          my: 0.5,
        }}
      />

      <Box sx={{ p: 2, pt: 2 }}>
        <Typography variant="h6" color={`${PureLightThemeCaaS.colors.alpha.black[100]}`} sx={{ textAlign: "center", mb: 1 }}>
          {/* {credits === 0 ? "You have " + credits + " credits left, you can pay with:" : "You can pay with:"} */}
          You can pay with:
        </Typography>

        {/* <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 7,
            pt: 0,
          }}
        > */}
        {/* Apple Pay */}
        {/* <Box
            component="img"
            src={ApplePayIcon}
            alt="Apple Pay"
            onClick={handleApplePayClick}
            sx={{
              width: "60px",
              height: "60px",
              objectFit: "contain",
              cursor: "pointer",
              opacity: showStripeForm ? 0.5 : 1,
              pointerEvents: showStripeForm ? "none" : "auto",
            }}
          /> */}
        {/* Google Pay */}
        {/* <Box
            component="img"
            src={GooglePayIcon}
            alt="Google Pay"
            onClick={handleGooglePayClick}
            sx={{
              width: "60px",
              height: "60px",
              // filter: 'brightness(0) invert(1)',
              objectFit: "contain",
              cursor: "pointer",
              opacity: showStripeForm ? 0.5 : 1,
              pointerEvents: showStripeForm ? "none" : "auto",
            }}
          />
        </Box> */}

        {/* Divider for credit card option */}
        {/* <Divider
          sx={{
            margin: {
              xs: "15px auto",
              md: "15px auto",
            },
            width: isMobile ? "100%" : "50%",
            bgcolor: "#CCCCCC",
            color: "#000000",
          }}
        ></Divider> */}

        {/* Primary path: hosted Stripe Checkout (handleCreditCardClick). Embedded Elements (CheckoutForm) only renders if showStripeForm is true, which the UI does not set today. */}
        {/* Credit Card Option */}
        {!showStripeForm ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              pt: 1,
            }}
          >
            <Button
              variant="outlined"
              startIcon={!isCreatingCheckout ? <CreditCardIcon /> : null}
              onClick={handleCreditCardClick}
              disabled={isCreatingCheckout}
              sx={{
                borderColor: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                color: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                borderRadius: 2,
                px: 2,
                py: 0,
                textTransform: "none",
                fontSize: "14px",
                fontWeight: "bold",
                height: "30px", // Fixed height to prevent size change
                width: "220px", // Fixed width to prevent size change (same with text or loader)
                "&:hover": {
                  borderColor: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                },
                "&.Mui-disabled": {
                  borderColor: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
                  color: `${PureLightThemeCaaS.colors.alpha.black[50]}`,
                },
              }}
            >
              {isCreatingCheckout ? <CircularProgress size={20} sx={{ color: `${PureLightThemeCaaS.colors.alpha.orange[100]}` }} /> : "Pay with Credit Card"}
            </Button>
          </Box>
        ) : stripePromise ? (
          <Elements stripe={stripePromise}>
            <Box
              sx={{
                maxWidth: "400px",
                margin: "0 auto",
                bgcolor: "white",
                borderRadius: 1,
                p: 2,
                mt: 2,
                position: "relative",
                overflow: "visible",
              }}
            >
              <CheckoutForm
                selectedProductId={selectedProductId || ""}
                promotionCodeId={promotionCodeId || undefined}
                promoPercentOff={promoPercentOff || undefined}
                promoAmountOff={promoAmountOff || undefined}
                onSuccess={handleStripeSuccess}
                onClose={() => setShowStripeForm(false)}
                onDrawerClose={onClose}
              />
            </Box>
          </Elements>
        ) : (
          <Box sx={{ textAlign: "center", p: 3 }}>
            <Typography color="error">Loading Stripe...</Typography>
          </Box>
        )}
      </Box>

      {/* Redirect to App Dialog */}
      <RedirectUsertoAppDialog open={showRedirectDialog} onClose={() => setShowRedirectDialog(false)} />

      <PaymentDialog
        open={showPaymentDialog}
        onClose={() => setShowPaymentDialog(false)}
        success={paymentDialogSuccess}
        message={paymentDialogMessage}
        autoCloseDelay={6000}
      />
    </>
  );
};

export default AddPaymentMethod;
